import React, { useEffect } from "react";
import LocalizedLink from "@/components/LocalizedLink";
import { useTranslation } from "react-i18next";
import { POSTS } from "@/data/posts";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import useScrollAnimation from "@/hooks/useScrollAnimation";
import NewsletterSignup from "@/components/NewsletterSignup";
import "./InsightsPage.scss";

const InsightsPage: React.FC = () => {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  useDocumentMeta({
    title: t("insights.pageTitle"),
    description: t("insights.pageDescription"),
    canonical: "https://devbloom.dev/insights",
    ogTitle: t("insights.pageTitle"),
    ogDescription: t("insights.pageDescription"),
    ogType: "website",
  });

  const { ref, isVisible } = useScrollAnimation({ threshold: 0.05 });

  return (
    <main className="insights-index">
      <div className="container">
        <header className="ii-header">
          <p className="ii-label">{t("insights.label")}</p>
          <h1 className="ii-heading">{t("insights.heading")}</h1>
          <p className="ii-sub">{t("insights.subheading")}</p>
        </header>

        <div ref={ref} className={`ii-grid${isVisible ? " visible" : ""}`}>
          {POSTS.map((post, idx) => {
            const postContent = t(`insights.posts.${post.slug}`, {
              returnObjects: true,
            }) as { lede: string } | null;

            return (
              <article key={post.slug} className="ii-card" style={{ "--i": idx } as React.CSSProperties}>
                <div className="ii-card-meta">
                  <time dateTime={post.date} className="ii-date">
                    {new Date(post.date).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </time>
                  <span className="ii-dot" aria-hidden="true">·</span>
                  <span className="ii-reading">
                    {t("insights.readingTime", { min: post.readingMinutes })}
                  </span>
                </div>

                <h2 className="ii-card-title">{post.title}</h2>

                <p className="ii-card-excerpt">
                  {postContent?.lede ?? post.excerpt}
                </p>

                <div className="ii-card-tags">
                  {post.tags.map((tag) => (
                    <span key={tag} className="ii-tag">{tag}</span>
                  ))}
                </div>

                <LocalizedLink
                  to={`/insights/${post.slug}`}
                  className="ii-read-link"
                >
                  {t("insights.readMore")} →
                </LocalizedLink>
              </article>
            );
          })}
        </div>

        <NewsletterSignup variant="card" />
      </div>
    </main>
  );
};

export default InsightsPage;
